import { Name } from "@app/types/enums";
import CASBlock from "@components/CASBlock";
import Container from "@components/Container/Container";
import Column from "@components/Grid/Column";
import Row from "@components/Grid/Row";
import MetaTags from "@components/MetaTags/MetaTags";
import useGenerateGiftURL from "@hooks/useGenerateGiftURL";
import { useGrapeshotData } from "@hooks/useGrapeshot";
import { useHookUpdateUrlArticle } from "@hooks/useHookUpdateUrlArticle";
import useShowGiftNofitifcationUpdate from "@hooks/useShowGiftNofitifcationUpdate";
import ArticleAttachmentsFromDrupal from "@pages/Article/components/ArticleAttachmentsFromDrupal";
import ArticleAuthorsList from "@pages/Article/components/ArticleAuthorsList";
import ArticleKeywords, {
  KeywordDisplayOption,
} from "@pages/Article/components/ArticleKeywords";
import ArticleKicker from "@pages/Article/components/ArticleKicker";
import ArticleParagraphs from "@pages/Article/components/ArticleParagraphs";
import ArticlesFromSameSection from "@pages/Article/components/ArticlesFromSameSection/ArticlesFromSameSection";
import ArticleSocialSharing from "@pages/Article/components/ArticleSocialSharing";
import ArticleSubscriberLabel from "@pages/Article/components/ArticleSubscriberLabel";
import ArticleSubshare from "@pages/Article/components/ArticleSubshare/ArticleSubshare";
import ArticleSubshareGiftingUpdate from "@pages/Article/components/ArticleSubshare/ArticleSubshareGiftingUpdate";
import ArticleSubshareSent from "@pages/Article/components/ArticleSubshare/ArticleSubshareSent";
import OutBrainWidget from "@pages/Article/components/OutBrainWidget";
import RelatedArticles from "@pages/Article/components/RelatedArticles/RelatedArticles";
import TelegramBanner from "@pages/Article/components/TelegramBanner";
import { ArticleProps } from "@pages/Article/types/Article";
import { checkDisplayPaywall } from "@pages/Article/utils/helpers";
import useOKTAUserStore from "@store/useOKTAUserStore";
import { CDP_PAYWALL_MODULE_ID } from "@util/constant";
import {
  getArticleBlurb,
  getArticleJSONLD,
  getFieldValue,
  getKickerFromArticleDataObject,
} from "@util/helpers";
import { find } from "lodash-es";

export default function ArticleDisplayInfographics({
  article,
  isGiftReceived,
  seeAlso,
  sectionNews,
  variant,
  index,
}: ArticleProps): React.ReactElement {
  // De-structure of article so it is cleaner to get the fields.
  const {
    id,
    title,
    updated: publishedDate,
    edited: updatedDate,
    authors,
    sections,
    tags,
    paywall,
    displaySetting,
    embedCodes,
    elements,
    relatedContributorProfile,
    sanitizedRelatedStories,
    shortUrl,
    urlPath,
  } = article;

  const kicker = getKickerFromArticleDataObject(article);
  const contentAccess = paywall?.contentAccess || "0";
  const displayPaywall = checkDisplayPaywall({
    isGiftReceived,
    variant: variant,
    contentAccess: contentAccess,
  });
  const showRelatedLinksAndNewsletterSignupForm =
    displaySetting?.showRelatedLinksAndNewsletterSignupForm || false;

  const standFirstField =
    find(elements, {
      type: "standfirst",
    })?.fields || [];
  const standFirst = getFieldValue(standFirstField, Name.Standfirst);
  const isPremium = contentAccess === "1";

  const OKTAUserInfo = useOKTAUserStore((state) => state.userInfo);
  const {
    alacrityRes,
    isModalOpen,
    setIsModalOpen,
    handleGenerateGiftURLModal,
  } = useGenerateGiftURL(isPremium);
  const { showGiftNotification, setShowGiftNotification } =
    useShowGiftNofitifcationUpdate(isPremium);
  const { gsChannels, gsChannelsNames } = useGrapeshotData(urlPath);
  const { ref } = useHookUpdateUrlArticle(urlPath);

  return (
    <>
      <MetaTags
        title={title}
        description={getArticleBlurb(article)}
        keywords={article?.tags?.map((tag) => tag.name)}
        slug={article.urlPath}
        shortLink={"/article/" + article.id}
        canonicalUrl={article?.seoFields?.canonicalUrl}
        imageUrl={article?.media?.[0]?.content?.fields?.["original-caas"]?.url}
        imageAlt={
          article?.media?.[0]?.summary?.find(
            (summaryItem) => summaryItem.key === "alttext"
          )?.value
        }
        jsonLd={getArticleJSONLD(article)}
        robots={"index, follow, max-image-preview:large"}
        gsChannels={gsChannels}
      />

      <ArticleSubshareGiftingUpdate
        isModalOpen={showGiftNotification}
        setIsModalOpen={setShowGiftNotification}
        {...{
          handleGenerateGiftURLModal,
        }}
      />

      <ArticleSubshareSent {...{ alacrityRes, isModalOpen, setIsModalOpen }} />

      <article
        className="display-type--infographics w-full py-5 font-public-sans"
        data-index={index}
        data-cueid={id}
        ref={ref}
      >
        <Container rootClassName="pt-4">
          <Row>
            <Column rootClassName="w-full">
              <div className="flex flex-wrap items-center space-x-1">
                <ArticleKicker kicker={kicker} />

                {contentAccess === "1" && kicker ? (
                  <div className="font-poppins text-4xs tracking-[1px] text-gray-650">
                    ·
                  </div>
                ) : null}

                <ArticleSubscriberLabel isPremium={contentAccess === "1"} />
              </div>

              <h1
                data-testid="article-title"
                className="mb-6 font-lct text-8xl font-bold leading-normal text-black md:text-12xl"
              >
                {title}
              </h1>

              <>
                {standFirst ? (
                  <p className="mb-6 mt-2 font-poppins">{standFirst}</p>
                ) : null}
              </>

              <>
                {publishedDate && updatedDate ? (
                  <ArticleAuthorsList
                    rootClassName="mb-6"
                    authors={authors}
                    contributors={relatedContributorProfile}
                    updatedDate={updatedDate}
                    publishedDate={publishedDate}
                    hasUpdatedTimeDisplayed={
                      displaySetting?.hasUpdatedTimeDisplayed
                    }
                  />
                ) : null}
              </>

              <div className="mb-6 flex items-center justify-between border-b border-t border-gray-250 py-4">
                {tags ? (
                  <ArticleKeywords
                    keywords={tags}
                    keywordDisplayOption={KeywordDisplayOption.First}
                  />
                ) : null}

                <div className="flex space-x-3">
                  <ArticleSocialSharing
                    articleTitle={title}
                    shareUrl={shortUrl}
                  />

                  <ArticleSubshare
                    isPremium={isPremium}
                    userType={OKTAUserInfo?.usertype}
                    {...{
                      handleGenerateGiftURLModal,
                      setIsModalOpen,
                      alacrityRes,
                    }}
                  />
                </div>
              </div>

              <ArticleAttachmentsFromDrupal
                cueId={article.id}
                rootClassName="mb-6"
              />

              <>
                {elements ? (
                  <ArticleParagraphs
                    id={id}
                    content={elements}
                    seeAlso={seeAlso}
                    embedCodes={embedCodes}
                    displayPaywall={displayPaywall}
                    displayRelatedLinksAndNewsletterSignupForm={
                      showRelatedLinksAndNewsletterSignupForm
                    }
                    rootClassName="text-lg"
                    keywords={tags}
                    sections={sections}
                    index={index}
                    gsChannelsNames={gsChannelsNames}
                  />
                ) : null}
              </>

              <>
                {contentAccess !== "1" ? (
                  <CASBlock cdpId={CDP_PAYWALL_MODULE_ID} index={index} />
                ) : null}
              </>

              <>
                {tags ? (
                  <ArticleKeywords
                    keywords={tags}
                    keywordDisplayOption={KeywordDisplayOption.Rest}
                  />
                ) : null}
              </>

              <RelatedArticles articles={sanitizedRelatedStories} />

              <TelegramBanner />

              <ArticlesFromSameSection data={sectionNews} sections={sections} />
            </Column>

            <>
              {article.urlPath ? (
                <OutBrainWidget
                  articleUrl={article.urlPath}
                  widgetId={index === 0 ? "AR_4" : "AR_5"}
                />
              ) : null}
            </>
          </Row>
        </Container>
      </article>
    </>
  );
}
